import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { IListInvoice, FinancialService } from "../../../../shared/services/api/financial/FinancialService";
import { GenericToolbar } from "../GenericToolbar";
import { IBlockInvoiceListRow, TableBlockInvoice } from "../TableBlockInvoice";

const normalizeDateOnly = (value?: string | null) => {
	const trimmedValue = value?.trim();
	return trimmedValue ? trimmedValue.slice(0, 10) : "";
};

const parseDateValue = (value?: string | null) => {
	const normalizedValue = normalizeDateOnly(value);
	if (!normalizedValue) return null;

	const [year, month, day] = normalizedValue.split("-").map(Number);
	if (!year || !month || !day) return null;

	const parsedDate = new Date(year, month - 1, day);
	const isValidDate = parsedDate.getFullYear() === year && parsedDate.getMonth() === month - 1 && parsedDate.getDate() === day;

	return isValidDate ? parsedDate : null;
};

const getTimeValue = (value?: string | null) => {
	const parsedDate = parseDateValue(value);
	return parsedDate ? parsedDate.getTime() : 0;
};

const formatDateForInput = (date: Date) => {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
	const today = new Date();
	return {
		start: formatDateForInput(new Date(today.getFullYear(), today.getMonth(), 1)),
		end: formatDateForInput(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
	};
};

const initialPeriod = getCurrentMonthRange();

const companyStatusOptions = [
	{ value: "todos", label: "Todos" },
	{ value: "pending", label: "Pendente" },
	{ value: "active", label: "Ativo" },
	{ value: "inactive", label: "Inativo" },
	{ value: "blocked", label: "Bloqueado" },
];

export const BlockInvoiceTab = () => {
	const toastHandler = useToast();
	const { debounce } = useDebounce();

	const [draftTextSearch, setDraftTextSearch] = useState("");
	const [draftDataIni, setDraftDataIni] = useState("");
	const [draftDataFim, setDraftDataFim] = useState("");
	const [draftStatus, setDraftStatus] = useState("todos");
	const [appliedFilters, setAppliedFilters] = useState({
		textSearch: "",
		dataIni: "",
		dataFim: "",
		status: "todos",
	});
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [rows, setRows] = useState<IListInvoice[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const handleFetchData = () => {
		setIsLoading(true);

		debounce(() => {
			FinancialService.getInvoiceBlockList()
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar bloqueios" />,
						});
						return;
					}

					setRows(response.data);
				})
				.finally(() => setIsLoading(false));
		});
	};

	useEffect(() => {
		handleFetchData();
	}, [refreshTrigger]);

	const groupedRows = useMemo<IBlockInvoiceListRow[]>(() => {
		const groupedMap = new Map<string, IListInvoice[]>();

		for (const row of rows) {
			const key = row.clientId || row.client?.id || row.id;
			const currentRows = groupedMap.get(key) ?? [];
			currentRows.push(row);
			groupedMap.set(key, currentRows);
		}

		return Array.from(groupedMap.entries()).map(([clientId, clientRows]) => {
			const sortedByCreatedAt = [...clientRows].sort((firstRow, secondRow) => getTimeValue(secondRow.createdAt) - getTimeValue(firstRow.createdAt));
			const sortedByDueDate = [...clientRows].sort((firstRow, secondRow) => getTimeValue(firstRow.dueDate) - getTimeValue(secondRow.dueDate));
			const representativeInvoice = sortedByCreatedAt[0];
			const firstDueInvoice = sortedByDueDate[0] ?? representativeInvoice;
			const reference = Array.from(new Set(clientRows.map((row) => row.reference).filter(Boolean))).join(", ");
			const groupedNumber = clientRows.reduce<number>((highestValue, row) => {
				const numericValue = Number(row.groupedNumber ?? 0);
				return Number.isFinite(numericValue) && numericValue > highestValue ? numericValue : highestValue;
			}, 0);

			return {
				clientId,
				client: representativeInvoice.client,
				createdAt: representativeInvoice.createdAt,
				dueDate: firstDueInvoice.dueDate,
				reference,
				groupedAt: clientRows.find((row) => row.groupedAt)?.groupedAt ?? null,
				groupedNumber: groupedNumber || undefined,
				representativeInvoice,
				invoices: clientRows,
			};
		});
	}, [rows]);

	const filteredRows = useMemo(() => {
		const normalizedSearch = appliedFilters.textSearch.trim().toLowerCase();
		const startDate = parseDateValue(appliedFilters.dataIni);
		const endDate = parseDateValue(appliedFilters.dataFim);

		if (endDate) {
			endDate.setHours(23, 59, 59, 999);
		}

		return groupedRows.filter((row) => {
			const rowDueDate = parseDateValue(row.dueDate);
			const matchesStatus = appliedFilters.status === "todos" || row.client?.status === appliedFilters.status;
			const matchesStartDate = !startDate || !rowDueDate || rowDueDate >= startDate;
			const matchesEndDate = !endDate || !rowDueDate || rowDueDate <= endDate;

			if (!matchesStatus || !matchesStartDate || !matchesEndDate) return false;

			if (!normalizedSearch) return true;

			const searchTarget = [row.clientId, row.reference, row.client?.name, row.client?.trade_name, row.client?.document, row.client?.email, row.client?.responsible_name]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();

			return searchTarget.includes(normalizedSearch);
		});
	}, [appliedFilters, groupedRows]);

	const paginatedRows = useMemo(() => {
		const startIndex = page * rowsPerPage;
		return filteredRows.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredRows, page, rowsPerPage]);

	const handleSearch = () => {
		setPage(0);
		setAppliedFilters({
			textSearch: draftTextSearch,
			dataIni: draftDataIni,
			dataFim: draftDataFim,
			status: draftStatus,
		});
		setRefreshTrigger((currentValue) => currentValue + 1);
	};

	const handleRowsPerPageChange = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
	};

	const handleBlockedClient = (clientId: string) => {
		setRows((currentRows) => currentRows.filter((row) => row.clientId !== clientId));
	};

	return (
		<Box>
			<GenericToolbar
				showInputSearch
				showDateRange
				showStatusSelect
				textSearch={draftTextSearch}
				onChangeTextSearch={setDraftTextSearch}
				onClearTextSearch={() => setDraftTextSearch("")}
				onChangeDateStart={setDraftDataIni}
				onChangeDateEnd={setDraftDataFim}
				onChangeStatus={setDraftStatus}
				statusOptions={companyStatusOptions}
				onClickRefresh={handleSearch}
			/>

			<LoadingComp isopen={isLoading} />
			<Box sx={{ overflowX: "auto" }}>
				<TableBlockInvoice
					rows={paginatedRows}
					page={page}
					rowsPerPage={rowsPerPage}
					totalCount={filteredRows.length}
					onPageChange={setPage}
					onRowsPerPageChange={handleRowsPerPageChange}
					onRefresh={handleSearch}
					onBlockedClient={handleBlockedClient}
				/>
			</Box>
		</Box>
	);
};